import { brandPalette as palette } from '../../../utils/brandPalette';
// src/components/auth/Login.jsx
// ============================================================================
// TelcoFair — Sign In
// Aesthetic: "Frosted Signal" — the same glass engine as CompanyDashboard,
// built around an explicit ELEVATION ladder so every card reads at a distinct
// depth above the aurora backdrop.
//
// Self-contained: all styling lives here, so ./Login.css is no longer needed.
// Auth logic (login) is unchanged.
// ============================================================================

import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Alert,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    IconButton,
    Divider,
    CircularProgress,
    alpha,
} from '@mui/material';
import {
    Email as EmailIcon,
    Visibility,
    VisibilityOff,
    Security as SecurityIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    Verified as VerifiedIcon,
    Shield as ShieldIcon,
    Lock as LockIcon,
    ArrowForward as ArrowForwardIcon,
    Storefront as StorefrontIcon,
    NetworkCheck as WifiIcon,
    NetworkCheck as CellTowerIcon,
    NetworkCheck as RouterIcon,
    SettingsApplications as HubIcon,
    Speed as SpeedIcon,
    Cloud as CloudIcon,
    Language as LanguageIcon,
    NetworkCheck as SignalIcon,
    ArrowUpward as CapsLockIcon,
    WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../AuthContext';
import BrandLogo from '../../common/BrandLogo';
import { friendlyError } from '../../../utils/userMessages';

// ============================================
// PALETTE — the login page's existing colours
// ============================================



const SHADOW = '25, 57, 78';     // warm ambient shadow, keyed to the ink/rust family
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BOUNCE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// ============================================================================
// ELEVATION LADDER
// The point of "card elevation glassmorphism": a pane's height above the page
// is expressed by FOUR things moving together, not just a bigger shadow.
//   1. ambient   — the wide, soft shadow that says "there is space under this"
//   2. contact   — a tight dark shadow directly beneath, anchoring it
//   3. blur      — panes further forward frost the backdrop harder
//   4. rake      — panes further forward catch more light on the top edge
// Levels 0-4. Everything on the page is placed on this ladder.
// ============================================================================

const ELEVATION = {
    0: { ambY: 2,  ambB: 8,   ambA: 0.05, conY: 1,  conB: 2,  conA: 0.04, blur: 8,  rake: 0.5,  fill: 0.34 },
    1: { ambY: 8,  ambB: 24,  ambA: 0.09, conY: 2,  conB: 6,  conA: 0.05, blur: 14, rake: 0.68, fill: 0.5 },
    2: { ambY: 16, ambB: 44,  ambA: 0.12, conY: 4,  conB: 12, conA: 0.07, blur: 20, rake: 0.82, fill: 0.62 },
    3: { ambY: 28, ambB: 70,  ambA: 0.15, conY: 7,  conB: 20, conA: 0.08, blur: 26, rake: 0.92, fill: 0.7 },
    4: { ambY: 42, ambB: 104, ambA: 0.18, conY: 11, conB: 30, conA: 0.09, blur: 32, rake: 1,    fill: 0.78 },
};

// The core frosted pane, placed on the ladder.
const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: '#FFFFFF',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.045) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.065)}`,
    boxShadow: 'none',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color,
    background: alpha(color, .07),
    border: `1px solid ${alpha(color, .10)}`,
    boxShadow: 'none',
});

// Frosted pill for chips and badges.
const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? '#fff' : color,
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

// ============================================================================
// GLOBAL CSS — transform/opacity only, so everything stays on the compositor.
// ============================================================================

const GLOBAL_CSS = `
    @keyframes tfAuroraA {
        0%, 100% { transform: translate3d(0,0,0) scale(1);          opacity: 0.55; }
        33%      { transform: translate3d(60px,-40px,0) scale(1.18); opacity: 0.85; }
        66%      { transform: translate3d(-40px,30px,0) scale(0.92); opacity: 0.5; }
    }
    @keyframes tfAuroraB {
        0%, 100% { transform: translate3d(0,0,0) scale(1.05);        opacity: 0.5; }
        40%      { transform: translate3d(-70px,45px,0) scale(0.88); opacity: 0.8; }
        75%      { transform: translate3d(35px,-30px,0) scale(1.2);  opacity: 0.62; }
    }
    @keyframes tfAuroraC {
        0%, 100% { transform: translate3d(0,0,0) scale(0.95);        opacity: 0.6; }
        50%      { transform: translate3d(50px,55px,0) scale(1.25);  opacity: 0.85; }
    }
    @keyframes tfRingSpin    { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
    @keyframes tfRingSpinRev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
    @keyframes tfShardRise {
        0%   { transform: translate3d(0,0,0) scale(0.6);       opacity: 0; }
        12%  { opacity: 0.9; }
        88%  { opacity: 0.45; }
        100% { transform: translate3d(22px,-84vh,0) scale(1.1); opacity: 0; }
    }
    @keyframes tfScanSweep {
        0%   { transform: translate3d(0,-12vh,0); opacity: 0; }
        8%   { opacity: 1; }
        92%  { opacity: 1; }
        100% { transform: translate3d(0,112vh,0); opacity: 0; }
    }
    @keyframes tfSignalWave {
        0%   { transform: scale(0.6); opacity: 0.8; }
        70%  { opacity: 0.12; }
        100% { transform: scale(2.2); opacity: 0; }
    }
    @keyframes tfBreathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.06); }
    }
    @keyframes tfSheen {
        0%   { transform: translateX(-130%) skewX(-18deg); }
        100% { transform: translateX(320%)  skewX(-18deg); }
    }
    @keyframes tfFloatIcon {
        0%, 100% { transform: translate3d(0,0,0) rotate(0deg);   opacity: 0.1; }
        50%      { transform: translate3d(0,-16px,0) rotate(5deg); opacity: 0.2; }
    }
    @keyframes tfGrain {
        0%   { transform: translate3d(0,0,0); }
        20%  { transform: translate3d(-2%,2%,0); }
        40%  { transform: translate3d(2%,-1%,0); }
        60%  { transform: translate3d(-1%,-2%,0); }
        80%  { transform: translate3d(1%,1%,0); }
        100% { transform: translate3d(0,0,0); }
    }
    @keyframes tfEdgeShift {
        0%   { background-position: 0% 0%; }
        100% { background-position: 300% 0%; }
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
`;

const FONT_HREF =
    'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700;9..144,900' +
    '&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';

let assetsInjected = false;
const injectAssets = () => {
    if (assetsInjected || typeof document === 'undefined') return;
    assetsInjected = true;

    const link = (rel, href, cross) => {
        const el = document.createElement('link');
        el.rel = rel;
        el.href = href;
        if (cross) el.crossOrigin = 'anonymous';
        document.head.appendChild(el);
    };
    link('preconnect', 'https://fonts.googleapis.com');
    link('preconnect', 'https://fonts.gstatic.com', true);
    link('stylesheet', FONT_HREF);

    const style = document.createElement('style');
    style.setAttribute('data-tf', 'login');
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
};

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ============================================================================
// PARTICLE NETWORK
// ============================================================================

const LINK_DIST = 150;

const ParticleNetwork = memo(() => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || prefersReducedMotion()) return undefined;

        const ctx = canvas.getContext('2d', { alpha: true });
        if (!ctx) return undefined;

        const TINTS = [
            [216, 78, 23],
            [165, 57, 12],
            [255, 140, 66],
            [52, 73, 94],
            [142, 68, 173],
        ];

        let w = 0;
        let h = 0;
        let particles = [];
        let raf = null;
        let running = true;

        const spawn = () => {
            const target = Math.min(90, Math.max(30, Math.round((w * h) / 22000)));
            particles = Array.from({ length: target }, () => {
                const t = TINTS[(Math.random() * TINTS.length) | 0];
                return {
                    x: Math.random() * w,
                    y: Math.random() * h,
                    r: Math.random() * 2 + 0.5,
                    vx: (Math.random() - 0.5) * 0.5,
                    vy: (Math.random() - 0.5) * 0.5,
                    a: Math.random() * 0.5 + 0.1,
                    c: `rgba(${t[0]}, ${t[1]}, ${t[2]},`,
                };
            });
        };

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = window.innerWidth;
            h = window.innerHeight;
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            spawn();
        };

        const cols = () => Math.max(1, Math.ceil(w / LINK_DIST));
        const rows = () => Math.max(1, Math.ceil(h / LINK_DIST));

        const drawLinks = () => {
            const nc = cols();
            const nr = rows();
            const buckets = new Array(nc * nr);
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                const cx = Math.min(nc - 1, Math.max(0, (p.x / LINK_DIST) | 0));
                const cy = Math.min(nr - 1, Math.max(0, (p.y / LINK_DIST) | 0));
                const k = cy * nc + cx;
                (buckets[k] || (buckets[k] = [])).push(p);
            }

            ctx.lineWidth = 0.5;
            for (let cy = 0; cy < nr; cy++) {
                for (let cx = 0; cx < nc; cx++) {
                    const here = buckets[cy * nc + cx];
                    if (!here) continue;
                    for (let dy = 0; dy <= 1; dy++) {
                        for (let dx = -1; dx <= 1; dx++) {
                            if (dy === 0 && dx < 0) continue;
                            const nx = cx + dx;
                            const ny = cy + dy;
                            if (nx < 0 || nx >= nc || ny >= nr) continue;
                            const other = buckets[ny * nc + nx];
                            if (!other) continue;
                            const same = dx === 0 && dy === 0;
                            for (let i = 0; i < here.length; i++) {
                                for (let j = same ? i + 1 : 0; j < other.length; j++) {
                                    const a = here[i];
                                    const b = other[j];
                                    const ddx = a.x - b.x;
                                    const ddy = a.y - b.y;
                                    const d2 = ddx * ddx + ddy * ddy;
                                    if (d2 >= LINK_DIST * LINK_DIST) continue;
                                    const d = Math.sqrt(d2);
                                    ctx.strokeStyle = `rgba(216, 78, 23, ${0.07 * (1 - d / LINK_DIST)})`;
                                    ctx.beginPath();
                                    ctx.moveTo(a.x, a.y);
                                    ctx.lineTo(b.x, b.y);
                                    ctx.stroke();
                                }
                            }
                        }
                    }
                }
            }
        };

        const frame = () => {
            if (!running) return;
            ctx.clearRect(0, 0, w, h);

            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0) { p.x = 0; p.vx *= -1; } else if (p.x > w) { p.x = w; p.vx *= -1; }
                if (p.y < 0) { p.y = 0; p.vy *= -1; } else if (p.y > h) { p.y = h; p.vy *= -1; }

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `${p.c} ${p.a})`;
                ctx.fill();
            }

            drawLinks();
            raf = requestAnimationFrame(frame);
        };

        const onVisibility = () => {
            if (document.hidden) {
                running = false;
                if (raf) cancelAnimationFrame(raf);
                raf = null;
            } else if (!running) {
                running = true;
                raf = requestAnimationFrame(frame);
            }
        };

        resize();
        raf = requestAnimationFrame(frame);
        window.addEventListener('resize', resize, { passive: true });
        document.addEventListener('visibilitychange', onVisibility);

        return () => {
            running = false;
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);

    return (
        <Box
            component="canvas"
            ref={canvasRef}
            aria-hidden="true"
            sx={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', opacity: 0.9 }}
        />
    );
});
ParticleNetwork.displayName = 'ParticleNetwork';

// ============================================================================
// AURORA BACKDROP
// ============================================================================

const CLOUDS = [
    { color: palette.rust,   size: 560, top: '-14%', left: '-4%', dur: 34, delay: 0,   anim: 'tfAuroraA', depth: 26 },
    { color: palette.ember,  size: 440, top: '8%',   left: '68%', dur: 42, delay: 3,   anim: 'tfAuroraB', depth: -18 },
    { color: palette.purple, size: 460, top: '58%',  left: '62%', dur: 46, delay: 5,   anim: 'tfAuroraA', depth: 22 },
    { color: palette.teal,   size: 380, top: '46%',  left: '-12%',dur: 30, delay: 1.5, anim: 'tfAuroraC', depth: -14 },
    { color: palette.slate,  size: 340, top: '26%',  left: '36%', dur: 36, delay: 2.5, anim: 'tfAuroraB', depth: 10 },
];

const GLYPHS = [
    { Icon: WifiIcon,       x: '10%', y: '15%', size: 28, dur: 6,   delay: 0 },
    { Icon: CellTowerIcon,  x: '85%', y: '20%', size: 32, dur: 7.5, delay: 0.5 },
    { Icon: RouterIcon,     x: '15%', y: '75%', size: 24, dur: 6.5, delay: 1 },
    { Icon: HubIcon,        x: '80%', y: '70%', size: 26, dur: 8,   delay: 1.5 },
    { Icon: SpeedIcon,      x: '50%', y: '10%', size: 22, dur: 7,   delay: 2 },
    { Icon: CloudIcon,      x: '70%', y: '85%', size: 30, dur: 6.8, delay: 2.5 },
    { Icon: LanguageIcon,   x: '25%', y: '90%', size: 26, dur: 7.2, delay: 3 },
    { Icon: SignalIcon,     x: '90%', y: '45%', size: 24, dur: 6.2, delay: 3.5 },
];

const SHARDS = [
    { s: 12, l: '18%', t: '78%', d: 18, delay: 0 },
    { s: 8,  l: '34%', t: '88%', d: 22, delay: 3 },
    { s: 16, l: '61%', t: '82%', d: 26, delay: 1.5 },
    { s: 10, l: '80%', t: '92%', d: 20, delay: 6 },
    { s: 7,  l: '46%', t: '96%', d: 24, delay: 8.5 },
    { s: 13, l: '92%', t: '72%', d: 28, delay: 4.5 },
];

const BroadcastRings = memo(({ size, opacity, reverse }) => (
    <Box component="svg" viewBox="0 0 400 400" sx={{ width: size, height: size, opacity, overflow: 'visible' }}>
        <g fill="none">
            <circle cx="200" cy="200" r="80" stroke={palette.rust} strokeWidth="1" strokeDasharray="3 10"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpinRev' : 'tfRingSpin'} 48s linear infinite` }} />
            <circle cx="200" cy="200" r="132" stroke={palette.ember} strokeWidth="1" strokeDasharray="1 14"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpin' : 'tfRingSpinRev'} 64s linear infinite` }} />
            <circle cx="200" cy="200" r="186" stroke={palette.purple} strokeWidth="1" strokeDasharray="6 18"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpinRev' : 'tfRingSpin'} 86s linear infinite` }} />
        </g>
    </Box>
));
BroadcastRings.displayName = 'BroadcastRings';

const AuroraBackdrop = memo(() => {
    const rootRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined' || prefersReducedMotion()) return undefined;
        const el = rootRef.current;
        if (!el) return undefined;

        let ticking = false;
        let mx = 0;
        let my = 0;

        const apply = () => {
            ticking = false;
            el.style.setProperty('--mx', mx.toFixed(4));
            el.style.setProperty('--my', my.toFixed(4));
        };

        const onMove = (e) => {
            mx = e.clientX / window.innerWidth - 0.5;
            my = e.clientY / window.innerHeight - 0.5;
            if (!ticking) {
                ticking = true;
                requestAnimationFrame(apply);
            }
        };

        window.addEventListener('mousemove', onMove, { passive: true });
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    return (
        <Box
            ref={rootRef}
            aria-hidden="true"
            sx={{
                position: 'fixed',
                inset: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                zIndex: 0,
                '--mx': 0,
                '--my': 0,
            }}
        >
            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    radial-gradient(780px circle at 6% -8%,   ${alpha(palette.rust, 0.15)}, transparent 60%),
                    radial-gradient(640px circle at 96% 8%,   ${alpha(palette.ember, 0.12)}, transparent 58%),
                    radial-gradient(700px circle at 50% 110%, ${alpha(palette.purple, 0.1)}, transparent 60%),
                    radial-gradient(540px circle at 82% 64%,  ${alpha(palette.teal, 0.09)}, transparent 58%)
                `,
            }} />

            {CLOUDS.map((c, i) => (
                <Box key={i} sx={{
                    position: 'absolute',
                    top: c.top,
                    left: c.left,
                    width: c.size,
                    height: c.size,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(c.color, 0.24)} 0%, ${alpha(c.color, 0.09)} 42%, transparent 70%)`,
                    animation: `${c.anim} ${c.dur}s ease-in-out ${c.delay}s infinite`,
                    willChange: 'transform',
                }}>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        transform: `translate3d(calc(var(--mx) * ${c.depth}px), calc(var(--my) * ${c.depth}px), 0)`,
                        transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
                    }} />
                </Box>
            ))}

            <Box sx={{
                position: 'absolute',
                inset: '-60px',
                opacity: 0.5,
                backgroundImage: `radial-gradient(circle, ${alpha(palette.ink, 0.09)} 1px, transparent 1px)`,
                backgroundSize: '30px 30px',
                maskImage: 'radial-gradient(ellipse 92% 74% at 50% 42%, #000 28%, transparent 78%)',
                WebkitMaskImage: 'radial-gradient(ellipse 92% 74% at 50% 42%, #000 28%, transparent 78%)',
                transform: 'translate3d(calc(var(--mx) * -14px), calc(var(--my) * -14px), 0)',
                transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
                willChange: 'transform',
            }} />

            <Box sx={{
                position: 'absolute',
                top: '-8%',
                right: '-14%',
                transform: 'translate3d(calc(var(--mx) * 30px), calc(var(--my) * 30px), 0)',
                transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}>
                <BroadcastRings size={640} opacity={0.42} />
            </Box>
            <Box sx={{
                position: 'absolute',
                bottom: '-20%',
                left: '-16%',
                transform: 'translate3d(calc(var(--mx) * -22px), calc(var(--my) * -22px), 0)',
                transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}>
                <BroadcastRings size={540} opacity={0.3} reverse />
            </Box>

            {GLYPHS.map(({ Icon, x, y, size, dur, delay }, i) => (
                <Box key={i} sx={{
                    position: 'absolute',
                    left: x,
                    top: y,
                    color: palette.rust,
                    opacity: 0.1,
                    animation: `tfFloatIcon ${dur}s ease-in-out ${delay}s infinite`,
                    willChange: 'transform, opacity',
                }}>
                    <Icon sx={{ fontSize: size }} />
                </Box>
            ))}

            {SHARDS.map((sh, i) => (
                <Box key={i} sx={{
                    position: 'absolute',
                    left: sh.l,
                    top: sh.t,
                    width: sh.s,
                    height: sh.s,
                    borderRadius: '50%',
                    background: 'linear-gradient(140deg, rgba(255,255,255,0.9), rgba(255,255,255,0.25))',
                    border: '1px solid rgba(255,255,255,0.72)',
                    boxShadow: `0 4px 14px ${alpha(palette.rust, 0.16)}`,
                    opacity: 0,
                    animation: `tfShardRise ${sh.d}s linear ${sh.delay}s infinite`,
                    willChange: 'transform, opacity',
                }} />
            ))}

            <Box sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                height: '28vh',
                background: `linear-gradient(180deg, transparent, ${alpha(palette.rust, 0.05)} 55%, transparent)`,
                animation: 'tfScanSweep 11s linear infinite',
                willChange: 'transform, opacity',
            }} />

            <Box sx={{
                position: 'absolute',
                inset: 0,
                opacity: 0.32,
                backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
                animation: 'tfGrain 9s steps(6) infinite',
                willChange: 'transform',
            }} />
            <Box sx={{
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(ellipse 120% 92% at 50% 45%, transparent 54%, ${alpha(palette.ink, 0.08)} 100%)`,
            }} />
        </Box>
    );
});
AuroraBackdrop.displayName = 'AuroraBackdrop';

// ============================================================================
// BRAND SIDE
// ============================================================================

const FEATURES = [
    { icon: <PeopleIcon />,     title: 'AI Matchmaking',      desc: 'Smart partner discovery', color: palette.rust },
    { icon: <ShieldIcon />,     title: 'Enterprise Security', desc: 'Bank-grade encryption',   color: palette.pine },
    { icon: <TrendingUpIcon />, title: 'Live Analytics',      desc: 'Real-time insights',      color: palette.purple },
];

const STATS = [
    { number: '1,200+', label: 'Companies' },
    { number: '5,000+', label: 'Users' },
    { number: '98%',    label: 'Satisfaction' },
];

const FeatureCard = memo(({ feature, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 + index * 0.09, ease: [0.16, 1, 0.3, 1] }}
    >
        <Box
            sx={{
                ...glassCard({ level: 1, radius: '18px', accent: feature.color }),
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                p: 1.75,
                overflow: 'hidden',
                transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                '&:hover': {
                    transform: 'translateY(-5px)',
                    ...glassLift(3, feature.color),
                },
                '&:hover .feat-pod': { transform: 'scale(1.08) rotate(-6deg)' },
                '&:hover .feat-sheen': { animation: `tfSheen 0.9s ${EASE}` },
            }}
        >
            <Box className="feat-sheen" sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: '40%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                transform: 'translateX(-130%) skewX(-18deg)',
                pointerEvents: 'none',
            }} />
            <Box className="feat-pod" sx={glassPod(feature.color, 44, '14px')}>
                {React.cloneElement(feature.icon, { sx: { fontSize: 21 } })}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: palette.ink, lineHeight: 1.25 }}>
                    {feature.title}
                </Typography>
                <Typography sx={{ fontSize: '0.76rem', color: palette.inkSoft }}>
                    {feature.desc}
                </Typography>
            </Box>
        </Box>
    </motion.div>
));
FeatureCard.displayName = 'FeatureCard';

const BrandSide = memo(() => (
    <motion.div
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 }, maxWidth: 560 }}>
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25 }}>
                    <Box sx={{ position: 'relative', width: 94, height: 94, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {[0, 1, 2].map((i) => (
                            <Box key={i} sx={{
                                position: 'absolute',
                                width: 86,
                                height: 86,
                                borderRadius: '50%',
                                border: `2px solid ${palette.rust}`,
                                opacity: 0,
                                animation: `tfSignalWave 3s cubic-bezier(0.2,0.6,0.3,1) ${i * 0.9}s infinite`,
                                willChange: 'transform, opacity',
                            }} />
                        ))}
                        <BrandLogo scope="platform" size={86} radius={22} alt="TELCOFAIR" sx={{ animation: 'tfBreathe 4s ease-in-out infinite', zIndex: 1 }} />
                    </Box>
                    <Box>
                        <Typography sx={{
                            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                            fontWeight: 900,
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                            letterSpacing: '0.06em',
                            color: palette.ink,
                            lineHeight: 1,
                        }}>
                            TELCOFAIR
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.76rem',
                            fontWeight: 700,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: palette.rust,
                            mt: 0.5,
                        }}>
                            Telecom Business Hub
                        </Typography>
                    </Box>
                </Box>
            </motion.div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.32 }}
            >
                <Typography sx={{
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 700,
                    fontSize: { xs: '2rem', md: '2.9rem' },
                    lineHeight: 1.14,
                    letterSpacing: '-0.02em',
                    color: palette.ink,
                }}>
                    Where Telecom{' '}
                    <Box component="span" sx={{
                        background: `linear-gradient(120deg, ${palette.rust}, ${palette.ember}, ${palette.purple})`,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                    }}>
                        Innovation
                    </Box>
                    <br />
                    Meets Opportunity
                </Typography>
                <Typography sx={{
                    mt: 2,
                    fontSize: { xs: '0.94rem', md: '1.02rem' },
                    lineHeight: 1.7,
                    color: palette.inkSoft,
                    maxWidth: 460,
                }}>
                    Join the premier B2B platform connecting telecom companies,
                    vendors, and industry leaders worldwide.
                </Typography>
            </motion.div>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {FEATURES.map((f, i) => (
                    <FeatureCard key={f.title} feature={f} index={i} />
                ))}
            </Box>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.9 }}
            >
                <Box sx={{
                    ...glassWell('18px'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-around',
                    p: 2.25,
                }}>
                    {STATS.map((s, i) => (
                        <React.Fragment key={s.label}>
                            {i > 0 && (
                                <Box sx={{
                                    width: '1px',
                                    alignSelf: 'stretch',
                                    background: `linear-gradient(180deg, transparent, ${alpha(palette.ink, 0.14)}, transparent)`,
                                }} />
                            )}
                            <Box sx={{ textAlign: 'center', px: 1 }}>
                                <Typography sx={{
                                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                    fontWeight: 700,
                                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                                    lineHeight: 1,
                                    background: `linear-gradient(135deg, ${palette.ink} 25%, ${palette.rust})`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                }}>
                                    {s.number}
                                </Typography>
                                <Typography sx={{
                                    mt: 0.5,
                                    fontSize: '0.68rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.1em',
                                    textTransform: 'uppercase',
                                    color: palette.inkFaint,
                                }}>
                                    {s.label}
                                </Typography>
                            </Box>
                        </React.Fragment>
                    ))}
                </Box>
            </motion.div>
        </Box>
    </motion.div>
));
BrandSide.displayName = 'BrandSide';

// ============================================================================
// FORM FIELD
// ============================================================================

const GlassField = ({ icon, focused, children }) => (
    <Box
        sx={{
            ...glassWell('16px', focused ? palette.rust : null),
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 0.5,
            transition: `box-shadow 0.32s ${EASE}, transform 0.32s ${EASE}, border-color 0.32s ease`,
            ...(focused && {
                transform: 'translateY(-2px)',
                boxShadow: `
                    inset 0 1px 6px rgba(${SHADOW}, 0.07),
                    0 8px 22px rgba(${SHADOW}, 0.1),
                    0 0 0 3px ${alpha(palette.rust, 0.14)}
                `,
            }),
            '& .field-pod': {
                transition: `all 0.32s ${BOUNCE}`,
                ...(focused && { transform: 'scale(1.06)' }),
            },
        }}
    >
        <Box
            className="field-pod"
            sx={
                focused
                    ? glassPod(palette.rust, 38, '12px')
                    : {
                          width: 38,
                          height: 38,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          color: palette.inkFaint,
                          background: alpha(palette.ink, 0.05),
                          border: `1px solid ${alpha('#ffffff', 0.5)}`,
                      }
            }
        >
            {React.cloneElement(icon, { sx: { fontSize: 19 } })}
        </Box>
        {children}
    </Box>
);

const inputSx = {
    '& .MuiInputLabel-root': {
        fontSize: '0.86rem',
        color: palette.inkFaint,
        '&.Mui-focused': { color: palette.rust },
    },
    '& .MuiInputBase-input': {
        fontSize: '0.94rem',
        color: palette.ink,
        fontWeight: 500,
        py: 1,
        '&::placeholder': { color: alpha(palette.inkFaint, 0.7), opacity: 1 },
        '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 100px ${alpha(palette.canvasAlt, 0.9)} inset`,
            WebkitTextFillColor: palette.ink,
            transition: 'background-color 9999s ease-in-out 0s',
        },
    },
};

const SECURITY_BADGES = [
    { icon: <LockIcon />,     label: 'AES-256' },
    { icon: <VerifiedIcon />, label: 'Verified' },
    { icon: <ShieldIcon />,   label: 'SSL/TLS' },
];

// ============================================================================
// LOGIN — FIXED: Using 'login' instead of 'loginWithEmail'
// ============================================================================

const Login = () => {
    const navigate = useNavigate();
    const { login, loading, publicRuntime, requestWhatsAppLoginOtp, loginWithWhatsAppOtp } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const [capsOn, setCapsOn] = useState(false);
    const [whatsAppPhone, setWhatsAppPhone] = useState('');
    const [whatsAppOtp, setWhatsAppOtp] = useState('');
    const [whatsAppOtpSent, setWhatsAppOtpSent] = useState(false);
    const authProvider = publicRuntime?.auth?.provider || 'firebase_auth';

    injectAssets();

    const busy = isLoading || loading;

    const handleEmailLogin = useCallback(
        async (e) => {
            e.preventDefault();
            setError('');
            setIsLoading(true);
            try {
                const result = await login(email, password, rememberMe);
                if (result?.requiresRegistration) {
                    toast.success('Welcome back. Continue from where you left off.');
                    navigate('/register');
                    return;
                }
                toast.success('Welcome back!');
                navigate('/dashboard');
            } catch (err) {
                setError(friendlyError(err, 'Unable to sign in. Please check your credentials and try again.'));
                setIsLoading(false);
            }
        },
        [email, password, rememberMe, login, navigate]  // FIXED: Updated dependency
    );

    const onPasswordKey = useCallback((e) => {
        if (typeof e.getModifierState === 'function') {
            setCapsOn(e.getModifierState('CapsLock'));
        }
    }, []);

    const handleWhatsAppRequest = useCallback(async () => {
        setError('');
        if (!whatsAppPhone.trim()) { setError('Enter the WhatsApp number registered with your TELCOFAIR account.'); return; }
        setIsLoading(true);
        try {
            await requestWhatsAppLoginOtp(whatsAppPhone.trim());
            setWhatsAppOtpSent(true);
            toast.success('WhatsApp verification code sent.');
        } catch (err) {
            setError(friendlyError(err, 'Unable to send the WhatsApp login code.'));
        } finally { setIsLoading(false); }
    }, [whatsAppPhone, requestWhatsAppLoginOtp]);

    const handleWhatsAppLogin = useCallback(async (e) => {
        e?.preventDefault?.();
        setError('');
        if (!whatsAppOtp.trim()) { setError('Enter the WhatsApp verification code.'); return; }
        setIsLoading(true);
        try {
            const result = await loginWithWhatsAppOtp(whatsAppPhone.trim(), whatsAppOtp.trim());
            if (result?.requiresRegistration) {
                toast.success('Welcome back. Continue from where you left off.');
                navigate('/register');
                return;
            }
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (err) {
            setError(friendlyError(err, 'Unable to sign in with WhatsApp.'));
            setIsLoading(false);
        }
    }, [whatsAppPhone, whatsAppOtp, loginWithWhatsAppOtp, navigate]);

    const pageSx = useMemo(
        () => ({
            minHeight: '100vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            bgcolor: palette.canvas,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            overflowX: 'hidden',
            py: { xs: 4, md: 6 },
        }),
        []
    );

    return (
        <Box sx={pageSx}>
            <AuroraBackdrop />
            <ParticleNetwork />

            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1.05fr 0.95fr', lg: '1.1fr 480px' },
                        gap: { xs: 5, md: 6, lg: 8 },
                        alignItems: 'center',
                    }}
                >
                    {/* Brand Side */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                        <BrandSide />
                    </Box>

                    {/* Compact brand lockup for small screens */}
                    <Box sx={{
                        ...glassCard({ level: 2, radius: '22px', accent: palette.rust }),
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center',
                        gap: 1.75,
                        justifyContent: 'center',
                        px: 2.5,
                        py: 2,
                    }}>
                        <BrandLogo scope="platform" size={64} radius={17} alt="TELCOFAIR" />
                        <Box>
                            <Typography sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontWeight: 900,
                                fontSize: '1.3rem',
                                letterSpacing: '0.06em',
                                color: palette.ink,
                                lineHeight: 1,
                            }}>
                                TELCOFAIR
                            </Typography>
                            <Typography sx={{
                                fontSize: '0.66rem',
                                fontWeight: 700,
                                letterSpacing: '0.13em',
                                textTransform: 'uppercase',
                                color: palette.rust,
                                mt: 0.4,
                            }}>
                                Telecom Business Hub
                            </Typography>
                        </Box>
                    </Box>

                    {/* Form card — elevation 4 */}
                    <motion.div
                        initial={{ opacity: 0, y: 34, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                ...glassCard({ level: 4, radius: '30px', accent: palette.rust }),
                                overflow: 'hidden',
                                p: { xs: 3, sm: 4 },
                                transition: `box-shadow 0.5s ${EASE}, transform 0.5s ${EASE}`,
                                '&:focus-within': {
                                    transform: 'translateY(-3px)',
                                    ...glassLift(4, palette.rust),
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: '6%',
                                    right: '6%',
                                    height: '2px',
                                    borderRadius: '0 0 999px 999px',
                                    background: `linear-gradient(90deg, transparent, ${alpha(palette.rust, 0.75)}, ${alpha(palette.ember, 0.75)}, ${alpha(palette.purple, 0.6)}, transparent)`,
                                    backgroundSize: '300% 100%',
                                    animation: 'tfEdgeShift 11s linear infinite',
                                    pointerEvents: 'none',
                                    zIndex: 3,
                                },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: -70,
                                    right: -70,
                                    width: 200,
                                    height: 200,
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${alpha(palette.rust, 0.16)}, transparent 70%)`,
                                    pointerEvents: 'none',
                                },
                            }}
                        >
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                {/* Header */}
                                <Box sx={{ textAlign: 'center', mb: 3.5 }}>
                                    <motion.div
                                        initial={{ scale: 0, rotate: -25 }}
                                        animate={{ scale: 1, rotate: 0 }}
                                        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.45 }}
                                        style={{ display: 'inline-block' }}
                                    >
                                        <Box sx={{ position: 'relative', width: 82, height: 82, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            {[0, 1].map((i) => (
                                                <Box key={i} sx={{
                                                    position: 'absolute',
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: '50%',
                                                    border: `1.5px solid ${palette.rust}`,
                                                    opacity: 0,
                                                    animation: `tfSignalWave 3.2s cubic-bezier(0.2,0.6,0.3,1) ${i * 1.1}s infinite`,
                                                }} />
                                            ))}
                                            <BrandLogo scope="platform" size={72} radius={19} alt="TELCOFAIR" sx={{ zIndex: 1 }} />
                                        </Box>
                                    </motion.div>

                                    <Typography sx={{
                                        mt: 2,
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                        fontWeight: 700,
                                        fontSize: { xs: '1.55rem', sm: '1.8rem' },
                                        color: palette.ink,
                                        letterSpacing: '-0.01em',
                                    }}>
                                        Welcome Back
                                    </Typography>
                                    <Typography sx={{ mt: 0.75, fontSize: '0.88rem', color: palette.inkSoft }}>
                                        Sign in to your company dashboard
                                    </Typography>
                                </Box>

                                {/* Error */}
                                <AnimatePresence initial={false}>
                                    {error && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -8, height: 0 }}
                                            animate={{ opacity: 1, y: 0, height: 'auto' }}
                                            exit={{ opacity: 0, y: -8, height: 0 }}
                                            transition={{ duration: 0.28 }}
                                        >
                                            <Alert
                                                severity="error"
                                                icon={<SecurityIcon />}
                                                onClose={() => setError('')}
                                                role="alert"
                                                sx={{
                                                    mb: 2.5,
                                                    borderRadius: '14px',
                                                    background: alpha(palette.danger, 0.1),
                                                    backdropFilter: 'blur(10px) saturate(160%)',
                                                    WebkitBackdropFilter: 'blur(10px) saturate(160%)',
                                                    border: `1px solid ${alpha(palette.danger, 0.28)}`,
                                                    boxShadow: `0 8px 22px ${alpha(palette.danger, 0.14)}, inset 0 1px 0 rgba(255,255,255,0.55)`,
                                                    color: palette.danger,
                                                    fontSize: '0.84rem',
                                                    fontWeight: 600,
                                                    '& .MuiAlert-icon': { color: palette.danger },
                                                }}
                                            >
                                                {error}
                                            </Alert>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Authentication form follows the Super Admin runtime choice. */}
                                {authProvider === 'whatsapp' ? (
                                    <Box component="form" onSubmit={handleWhatsAppLogin} noValidate>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <GlassField icon={<WhatsAppIcon />} focused={focusedField === 'whatsappPhone'}>
                                                <TextField
                                                    fullWidth
                                                    label="WhatsApp Number"
                                                    value={whatsAppPhone}
                                                    onChange={(e) => setWhatsAppPhone(e.target.value)}
                                                    required
                                                    autoFocus
                                                    placeholder="+91 98765 43210"
                                                    onFocus={() => setFocusedField('whatsappPhone')}
                                                    onBlur={() => setFocusedField(null)}
                                                    variant="standard"
                                                    slotProps={{ input: { disableUnderline: true } }}
                                                    sx={inputSx}
                                                />
                                            </GlassField>
                                            {whatsAppOtpSent && (
                                                <GlassField icon={<LockIcon />} focused={focusedField === 'whatsappOtp'}>
                                                    <TextField
                                                        fullWidth
                                                        label="WhatsApp OTP"
                                                        value={whatsAppOtp}
                                                        onChange={(e) => setWhatsAppOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
                                                        required
                                                        placeholder="Enter verification code"
                                                        onFocus={() => setFocusedField('whatsappOtp')}
                                                        onBlur={() => setFocusedField(null)}
                                                        variant="standard"
                                                        slotProps={{ input: { disableUnderline: true } }}
                                                        sx={inputSx}
                                                    />
                                                </GlassField>
                                            )}
                                        </Box>
                                        <Box sx={{ mt: 2, mb: 3, p: 1.35, borderRadius: '13px', bgcolor: alpha(palette.pine, .06), border: `1px solid ${alpha(palette.pine,.12)}` }}>
                                            <Typography sx={{ fontSize: '.78rem', lineHeight: 1.55, color: palette.inkSoft }}>
                                                Use the verification code sent to your WhatsApp number to sign in.
                                            </Typography>
                                        </Box>
                                        {!whatsAppOtpSent ? (
                                            <Button fullWidth type="button" onClick={handleWhatsAppRequest} disabled={busy} startIcon={<WhatsAppIcon />} sx={{ py: 1.55, borderRadius: '16px', textTransform: 'none', fontWeight: 850, color: '#fff !important', background: `linear-gradient(135deg, ${palette.pine}, ${palette.rustDeep})` }}>
                                                {busy ? 'Sending code…' : 'Send WhatsApp Code'}
                                            </Button>
                                        ) : (
                                            <Box sx={{ display: 'grid', gap: 1 }}>
                                                <Button fullWidth type="submit" disabled={busy} startIcon={<WhatsAppIcon />} sx={{ py: 1.55, borderRadius: '16px', textTransform: 'none', fontWeight: 850, color: '#fff !important', background: `linear-gradient(135deg, ${palette.pine}, ${palette.rustDeep})` }}>
                                                    {busy ? 'Verifying…' : 'Verify & Sign In'}
                                                </Button>
                                                <Button type="button" onClick={handleWhatsAppRequest} disabled={busy} sx={{ textTransform: 'none', fontWeight: 750, color: palette.rust }}>Resend code</Button>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                <Box component="form" onSubmit={handleEmailLogin} noValidate>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <GlassField icon={<EmailIcon />} focused={focusedField === 'email'}>
                                            <TextField
                                                fullWidth
                                                label="Email Address"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                autoFocus
                                                placeholder="name@company.com"
                                                onFocus={() => setFocusedField('email')}
                                                onBlur={() => setFocusedField(null)}
                                                variant="standard"
                                                autoComplete="email"
                                                slotProps={{ input: { disableUnderline: true } }}
                                                sx={inputSx}
                                            />
                                        </GlassField>

                                        <Box>
                                            <GlassField icon={<LockIcon />} focused={focusedField === 'password'}>
                                                <TextField
                                                    fullWidth
                                                    label="Password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    onKeyUp={onPasswordKey}
                                                    onKeyDown={onPasswordKey}
                                                    required
                                                    placeholder="Enter your password"
                                                    onFocus={() => setFocusedField('password')}
                                                    onBlur={() => { setFocusedField(null); setCapsOn(false); }}
                                                    variant="standard"
                                                    autoComplete="current-password"
                                                    slotProps={{ input: {
                                                        disableUnderline: true,
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() => setShowPassword((v) => !v)}
                                                                    edge="end"
                                                                    size="small"
                                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                                    sx={{
                                                                        color: palette.inkFaint,
                                                                        borderRadius: '10px',
                                                                        transition: 'all 0.25s ease',
                                                                        '&:hover': { color: palette.rust, background: alpha(palette.rust, 0.09) },
                                                                    }}
                                                                >
                                                                    {showPassword
                                                                        ? <VisibilityOff sx={{ fontSize: 19 }} />
                                                                        : <Visibility sx={{ fontSize: 19 }} />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    } }}
                                                    sx={inputSx}
                                                />
                                            </GlassField>

                                            <AnimatePresence>
                                                {capsOn && (
                                                    <motion.div
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.22 }}
                                                    >
                                                        <Box sx={{
                                                            ...glassPill(palette.gold),
                                                            mt: 1,
                                                            ml: 0.5,
                                                            px: 1.2,
                                                            height: 24,
                                                            fontSize: '0.68rem',
                                                        }}>
                                                            <CapsLockIcon sx={{ fontSize: 14 }} />
                                                            Caps Lock is on
                                                        </Box>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </Box>
                                    </Box>

                                    {/* Options */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        mt: 2,
                                        mb: 3,
                                    }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={rememberMe}
                                                    onChange={(e) => setRememberMe(e.target.checked)}
                                                    size="small"
                                                    sx={{
                                                        color: alpha(palette.inkFaint, 0.7),
                                                        '&.Mui-checked': { color: palette.rust },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography sx={{ fontSize: '0.82rem', color: palette.inkSoft, fontWeight: 500 }}>
                                                    Remember me
                                                </Typography>
                                            }
                                        />
                                        <Box
                                            component={Link}
                                            to="/reset-password"
                                            sx={{
                                                fontSize: '0.82rem',
                                                fontWeight: 700,
                                                color: palette.rust,
                                                textDecoration: 'none',
                                                borderRadius: '999px',
                                                px: 1.25,
                                                py: 0.5,
                                                transition: 'all 0.25s ease',
                                                '&:hover': { background: alpha(palette.rust, 0.1) },
                                            }}
                                        >
                                            Forgot Password?
                                        </Box>
                                    </Box>

                                    {/* Submit */}
                                    <Button
                                        type="submit"
                                        fullWidth
                                        disabled={busy}
                                        endIcon={!busy && <ArrowForwardIcon sx={{ fontSize: 19 }} />}
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            py: 1.6,
                                            borderRadius: '16px',
                                            textTransform: 'none',
                                            fontSize: '0.98rem',
                                            fontWeight: 800,
                                            color: '#fff !important',
                                            '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: '#fff !important' },
                                            background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                                            border: '1px solid rgba(255,255,255,0.28)',
                                            boxShadow: `
                                                0 16px 36px ${alpha(palette.rust, 0.36)},
                                                0 5px 12px ${alpha(palette.rust, 0.22)},
                                                inset 0 1px 0 rgba(255,255,255,0.5),
                                                inset 0 -12px 22px ${alpha(palette.rustDeep, 0.44)}
                                            `,
                                            transition: `transform 0.3s ${BOUNCE}, box-shadow 0.3s ease`,
                                            '&:hover': {
                                                transform: 'translateY(-3px)',
                                                boxShadow: `
                                                    0 24px 50px ${alpha(palette.rust, 0.44)},
                                                    0 8px 18px ${alpha(palette.rust, 0.26)},
                                                    inset 0 1px 0 rgba(255,255,255,0.6),
                                                    inset 0 -12px 22px ${alpha(palette.rustDeep, 0.44)}
                                                `,
                                            },
                                            '&:hover .btn-sheen': { animation: `tfSheen 0.85s ${EASE}` },
                                            '&:active': { transform: 'translateY(-1px)' },
                                            '&.Mui-disabled': {
                                                color: alpha('#ffffff', 0.85),
                                                background: `linear-gradient(140deg, ${alpha(palette.rust, 0.6)}, ${alpha(palette.rustDeep, 0.6)})`,
                                            },
                                        }}
                                    >
                                        <Box className="btn-sheen" sx={{
                                            position: 'absolute',
                                            top: 0,
                                            bottom: 0,
                                            left: 0,
                                            width: '38%',
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.42), transparent)',
                                            transform: 'translateX(-130%) skewX(-18deg)',
                                            pointerEvents: 'none',
                                        }} />
                                        {busy ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <CircularProgress size={19} sx={{ color: '#fff' }} />
                                                <span>Signing in…</span>
                                            </Box>
                                        ) : (
                                            'Sign In'
                                        )}
                                    </Button>
                                </Box>
                                )}

                                {/* Security badges */}
                                <Box sx={{ mt: 3.5 }}>
                                    <Divider sx={{
                                        '&::before, &::after': { borderColor: alpha(palette.ink, 0.08) },
                                    }}>
                                        <Typography sx={{
                                            fontSize: '0.66rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.14em',
                                            textTransform: 'uppercase',
                                            color: palette.inkFaint,
                                        }}>
                                            Secured Platform
                                        </Typography>
                                    </Divider>

                                    <Box sx={{
                                        ...glassCard({ level: 0, radius: '16px' }),
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: 1,
                                        mt: 2,
                                        p: 1.5,
                                        flexWrap: 'wrap',
                                    }}>
                                        {SECURITY_BADGES.map((badge, i) => (
                                            <motion.div
                                                key={badge.label}
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.9 + i * 0.1, duration: 0.4 }}
                                            >
                                                <Box sx={{
                                                    ...glassPill(palette.pine),
                                                    px: 1.4,
                                                    height: 28,
                                                    fontSize: '0.7rem',
                                                    letterSpacing: '0.03em',
                                                }}>
                                                    {React.cloneElement(badge.icon, { sx: { fontSize: 14 } })}
                                                    {badge.label}
                                                </Box>
                                            </motion.div>
                                        ))}
                                    </Box>
                                </Box>

                                {/* Register CTA */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 1.15 }}
                                >
                                    <Box sx={{
                                        ...glassWell('18px'),
                                        mt: 3,
                                        p: 2,
                                        textAlign: 'center',
                                    }}>
                                        <Typography sx={{ fontSize: '0.83rem', color: palette.inkSoft, mb: 1.25 }}>
                                            New to TELCOFAIR?
                                        </Typography>
                                        <Button
                                            component={Link}
                                            to="/register"
                                            startIcon={<StorefrontIcon sx={{ fontSize: 18 }} />}
                                            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 800,
                                                fontSize: '0.86rem',
                                                color: '#fff !important',
                                                '& .MuiButton-startIcon, & .MuiButton-endIcon': { color: '#fff !important' },
                                                borderRadius: '999px',
                                                px: 2.5,
                                                py: 0.9,
                                                background: `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                backdropFilter: 'blur(10px) saturate(175%)',
                                                WebkitBackdropFilter: 'blur(10px) saturate(175%)',
                                                border: '1px solid rgba(255,255,255,.26)',
                                                boxShadow: `0 10px 24px ${alpha(palette.rust, 0.24)}, inset 0 1px 0 rgba(255,255,255,0.32)`,
                                                transition: `transform 0.3s ${BOUNCE}, box-shadow 0.3s ease`,
                                                '&:hover': {
                                                    transform: 'translateY(-3px)',
                                                    background: `linear-gradient(135deg, ${palette.rustDeep}, ${palette.rust})`,
                                                    borderColor: 'rgba(255,255,255,.34)',
                                                    boxShadow: `0 14px 30px rgba(${SHADOW}, 0.14), 0 0 26px ${alpha(palette.rust, 0.18)}, inset 0 1px 0 rgba(255,255,255,0.9)`,
                                                },
                                                '&:hover .MuiButton-endIcon': { transform: 'translateX(3px)' },
                                                '& .MuiButton-endIcon': { transition: 'transform 0.28s ease' },
                                            }}
                                        >
                                            Create Account
                                        </Button>
                                    </Box>
                                </motion.div>
                            </Box>
                        </Paper>
                    </motion.div>
                </Box>
            </Container>
        </Box>
    );
};

export default Login;